import React, { useEffect, useState } from "react";
import { Toaster, toast } from "sonner";
import { AdminShell } from "@/components/AdminShell";
import { RefreshCw, Rocket, CheckCircle2, AlertTriangle, Clock, Mail } from "lucide-react";
import { Panel, Eyebrow, MonoLabel, KPI } from "@/components/Panel";
import { AdminLogin, useAdminSession } from "@/pages/admin/AdminLogin";
import { softLaunchApi } from "@/lib/softLaunchApi";
import { apiErr } from "@/lib/authApi";

const inr = (p) => `₹${((p || 0) / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
const clock = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

const PAY_TONE = { captured: "var(--green, #3ec97a)", failed: "var(--red-2)", created: "var(--taupe)", authorized: "var(--taupe)", none: "var(--muted)" };

export default function SoftLaunchMonitor() {
  const { user, setUser } = useAdminSession();
  if (!user) return <AdminLogin onSuccess={setUser} />;
  return <Console />;
}

function Console() {
  const [data, setData] = useState(null);
  const [includeAll, setIncludeAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(null);

  const resend = async (regId) => {
    setResending(regId);
    try {
      const r = await softLaunchApi.resendInvoice(regId);
      toast.success(`Invoice ${r.invoice} emailed to ${r.email}`);
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setResending(null); }
  };

  const load = async (all = includeAll) => {
    setBusy(true);
    try { setData(await softLaunchApi.monitor(all)); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => {
    const t = setInterval(() => load(), 20000);
    return () => clearInterval(t);
  }, [includeAll]); // eslint-disable-line

  const totals = data?.totals || {};
  const rows = data?.rows || [];

  return (
    <AdminShell>
      <Toaster theme="dark" position="top-right" />
      <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <Eyebrow className="mt-4">Soft Launch · live pilot</Eyebrow>
            <h1 className="h-hero mt-3 text-4xl sm:text-5xl">Watch the first real payments land.</h1>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="sl-toggle-scope" className="btn-ghost"
              onClick={() => { const v = !includeAll; setIncludeAll(v); load(v); }}>
              {includeAll ? "Showing: all registrations" : "Showing: pilot only"}
            </button>
            <button data-testid="sl-refresh-btn" className="btn-red" disabled={busy} onClick={() => load()}>
              <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 lg:grid-cols-6 gap-0">
          <KPI label="Registrations" value={totals.registrations ?? "—"} />
          <KPI label="Paid" value={totals.paid ?? "—"} />
          <KPI label="Payment pending" value={totals.payment_pending ?? "—"} />
          <KPI label="Payment failed" value={totals.payment_failed ?? "—"} />
          <KPI label="WhatsApp failed" value={totals.whatsapp_failed ?? "—"} />
          <KPI label="Invoices" value={totals.invoices ?? "—"} />
        </div>

        <Panel className="mt-8 p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <MonoLabel>
              <Rocket size={13} className="inline mr-2" />
              {data?.config?.enabled ? `Invite-only ON · ${data.config.allowed_phones?.length ?? 0} invited` : "Invite-only OFF · registration open to everyone"}
            </MonoLabel>
            <MonoLabel style={{ color: "var(--taupe)" }}>Auto-refreshing every 20s · last {clock(data?.fetched_at)}</MonoLabel>
          </div>

          <div className="mt-5 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left mono-label">
                  <th className="py-3 pr-3">Who</th>
                  <th className="py-3 pr-3">Registration</th>
                  <th className="py-3 pr-3">Payment</th>
                  <th className="py-3 pr-3">Amount</th>
                  <th className="py-3 pr-3">Invoice</th>
                  <th className="py-3 pr-3">WhatsApp</th>
                  <th className="py-3 pr-3">When</th>
                  <th className="py-3">Invoice email</th>
                </tr>
              </thead>
              <tbody>
                {!data && <tr><td colSpan={8} className="py-8 mono-label" data-testid="sl-loading">Loading pilot activity…</td></tr>}
                {data && rows.length === 0 && (
                  <tr><td colSpan={8} className="py-8 mono-label" style={{ color: "var(--taupe)" }} data-testid="sl-empty">
                    No pilot registrations yet — turn on invite-only in DevAdmin → Soft Launch and share the link with your first group.
                  </td></tr>
                )}
                {rows.map((r, i) => (
                  <tr key={r.registration_id} className="hairline-t" data-testid={`sl-row-${i}`}>
                    <td className="py-3 pr-3">
                      <div>{r.name}</div>
                      <div className="mono-label" style={{ color: "var(--taupe)" }}>{r.phone} · {r.registration_type}</div>
                    </td>
                    <td className="py-3 pr-3 mono-label">{r.status}</td>
                    <td className="py-3 pr-3 mono-label" style={{ color: PAY_TONE[r.payment_state] || "var(--text)" }} data-testid={`sl-pay-${i}`}>
                      {r.payment_state === "captured" ? <CheckCircle2 size={13} className="inline mr-1" />
                        : r.payment_state === "failed" ? <AlertTriangle size={13} className="inline mr-1" />
                        : <Clock size={13} className="inline mr-1" />}
                      {r.payment_state}
                    </td>
                    <td className="py-3 pr-3">{r.payment ? inr(r.payment.amount_paise) : "—"}</td>
                    <td className="py-3 pr-3 mono-label" style={{ color: r.invoice_number ? "var(--text)" : "var(--muted)" }}>{r.invoice_number || "—"}</td>
                    <td className="py-3 pr-3">
                      {r.whatsapp.length === 0 ? <span className="mono-label" style={{ color: "var(--muted)" }}>—</span> : (
                        <div className="flex flex-wrap gap-1">
                          {r.whatsapp.map((m, j) => (
                            <span key={j} className="mono-label px-2 py-0.5 hairline" data-testid={`sl-wa-${i}-${j}`}
                              style={{ color: m.status === "failed" ? "var(--red-2)" : m.status === "delivered" || m.status === "read" || m.status === "sent" ? "var(--text)" : "var(--taupe)" }}
                              title={m.failure_reason || m.status}>
                              {m.template_key} · {m.status}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="py-3 mono-label" style={{ color: "var(--taupe)" }}>{clock(r.paid_at || r.created_at)}</td>
                    <td className="py-3">
                      {r.payment_state === "captured" ? (
                        <button data-testid={`sl-resend-invoice-${i}`} className="btn-ghost !py-1 !px-2 mono-label"
                          disabled={resending === r.registration_id} onClick={() => resend(r.registration_id)}>
                          <Mail size={12} /> {resending === r.registration_id ? "Sending…" : "Resend"}
                        </button>
                      ) : <span className="mono-label" style={{ color: "var(--muted)" }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mono-label mt-5" style={{ color: "var(--muted)" }}>
            Failed payments, payments stuck over 2 hours, and failed WhatsApp sends also raise an alert in the
            dashboard alert history (scanned by the <code>soft-launch-monitor</code> cron every 15 minutes).
          </div>
        </Panel>
      </div>
    </AdminShell>
  );
}
